import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { sandataAPI, clientsAPI } from "../lib/api";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  ArrowLeft,
  Plus,
  RefreshCw,
  Link2,
  Trash2,
  Edit,
  Search,
  Users,
  UserCircle,
  Calendar,
  Filter,
} from "lucide-react";

const MAPPING_TYPE_LABELS = {
  CLIENT: { label: "Client", icon: Users, color: "bg-blue-100 text-blue-800" },
  CAREGIVER: { label: "Caregiver", icon: UserCircle, color: "bg-green-100 text-green-800" },
  VISIT: { label: "Visit", icon: Calendar, color: "bg-purple-100 text-purple-800" },
};

const MATCH_METHOD_LABELS = {
  manual: { label: "Manual", color: "bg-slate-100 text-slate-800" },
  import: { label: "Import", color: "bg-blue-100 text-blue-800" },
  heuristic: { label: "Auto-Matched", color: "bg-amber-100 text-amber-800" },
};

const SandataMappingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  
  // Form state
  const [form, setForm] = useState({
    mapping_type: "CLIENT",
    sandata_id: "",
    internal_id: "",
    sandata_name: "",
    internal_name: "",
  });
  
  // Lookup lists for internal IDs
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  
  const canManage = ["Admin", "ComplianceManager"].includes(user?.role);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.mapping_type = typeFilter;
      if (includeArchived) params.include_archived = true;
      
      const res = await sandataAPI.getMappings(params);
      let filtered = res.data.mappings || [];
      
      // Client-side search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter(m => 
          m.sandata_id?.toLowerCase().includes(q) ||
          m.sandata_name?.toLowerCase().includes(q) ||
          m.internal_name?.toLowerCase().includes(q)
        );
      }
      
      setMappings(filtered);
      setTotal(res.data.total || filtered.length);
    } catch (error) {
      toast.error("Failed to load mappings");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, includeArchived, searchQuery]);

  const fetchLookups = useCallback(async () => {
    try {
      const [clientsRes, usersRes] = await Promise.all([
        clientsAPI.list({ page_size: 200 }),
        api.get('/users', { params: { page_size: 200 } }),
      ]);
      setClients(clientsRes.data.clients || []);
      setCaregivers((usersRes.data.users || []).filter(u => u.role === "Caregiver" && !u.is_archived));
    } catch (error) {
      console.log("Could not load lookup data");
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useEffect(() => {
    if (canManage) fetchLookups();
  }, [canManage, fetchLookups]);

  const handleCreate = async () => {
    if (!form.sandata_id || !form.internal_id) {
      toast.error("Sandata ID and Internal ID are required");
      return;
    }
    
    try {
      await sandataAPI.createMapping({
        mapping_type: form.mapping_type,
        sandata_id: form.sandata_id,
        internal_id: form.internal_id,
        sandata_name: form.sandata_name || null,
        internal_name: form.internal_name || null,
        match_method: "manual",
      });
      toast.success("Mapping created");
      setShowCreateModal(false);
      setForm({ mapping_type: "CLIENT", sandata_id: "", internal_id: "", sandata_name: "", internal_name: "" });
      fetchMappings();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create mapping";
      toast.error(msg);
    }
  };

  const handleUpdate = async () => {
    if (!selectedMapping) return;
    
    try {
      await sandataAPI.updateMapping(selectedMapping.id, {
        internal_id: form.internal_id,
        internal_name: form.internal_name,
      });
      toast.success("Mapping updated");
      setShowEditModal(false);
      setSelectedMapping(null);
      fetchMappings();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to update mapping";
      toast.error(msg);
    }
  };

  const handleArchive = async () => {
    if (!selectedMapping || archiveReason.length < 5) {
      toast.error("Please provide a reason (min 5 characters)");
      return;
    }
    
    try {
      await sandataAPI.archiveMapping(selectedMapping.id, archiveReason);
      toast.success("Mapping archived");
      setShowArchiveModal(false);
      setSelectedMapping(null);
      setArchiveReason("");
      fetchMappings();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to archive mapping";
      toast.error(msg);
    }
  };

  const openEditModal = (mapping) => {
    setSelectedMapping(mapping);
    setForm({
      mapping_type: mapping.mapping_type,
      sandata_id: mapping.sandata_id,
      internal_id: mapping.internal_id,
      sandata_name: mapping.sandata_name || "",
      internal_name: mapping.internal_name || "",
    });
    setShowEditModal(true);
  };

  const openArchiveModal = (mapping) => {
    setSelectedMapping(mapping);
    setShowArchiveModal(true);
  };

  const getInternalOptions = () => {
    if (form.mapping_type === "CLIENT") {
      return clients.map(c => ({
        value: c.id,
        label: `${c.last_name}, ${c.first_name} (${c.medicaid_id || 'No MID'})`
      }));
    } else if (form.mapping_type === "CAREGIVER") {
      return caregivers.map(c => ({
        value: c.id,
        label: `${c.last_name}, ${c.first_name} (${c.email})`
      }));
    }
    return [];
  };

  return (
    <Layout title="Sandata Mappings">
      <div className="space-y-6" data-testid="sandata-mappings-page">
        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            Manage mappings between Sandata IDs and internal records
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchMappings} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            {canManage && (
              <Button onClick={() => setShowCreateModal(true)} className="bg-avida-blue hover:bg-avida-blue-800" data-testid="create-mapping-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Mapping
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="CLIENT">Client</SelectItem>
                    <SelectItem value="CAREGIVER">Caregiver</SelectItem>
                    <SelectItem value="VISIT">Visit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by ID or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              Include Archived
            </label>
            
            <div className="ml-auto text-sm text-slate-500">
              {total} mapping{total !== 1 ? "s" : ""}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Sandata ID</TableHead>
                <TableHead>Sandata Name</TableHead>
                <TableHead>Internal ID</TableHead>
                <TableHead>Internal Name</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Created</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} className="text-center py-8 text-slate-500">
                    {loading ? "Loading..." : "No mappings found"}
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((m) => {
                  const typeInfo = MAPPING_TYPE_LABELS[m.mapping_type] || {};
                  const methodInfo = MATCH_METHOD_LABELS[m.match_method] || {};
                  const TypeIcon = typeInfo.icon || Link2;
                  
                  return (
                    <TableRow 
                      key={m.id} 
                      className={m.archived_at ? "opacity-50" : ""}
                      data-testid={`mapping-row-${m.id}`}
                    >
                      <TableCell>
                        <Badge className={typeInfo.color}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {typeInfo.label || m.mapping_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{m.sandata_id}</TableCell>
                      <TableCell>{m.sandata_name || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">{m.internal_id?.substring(0, 8)}...</TableCell>
                      <TableCell>{m.internal_name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={methodInfo.color}>
                          {methodInfo.label || m.match_method}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(m.created_at).toLocaleDateString()}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          {!m.archived_at && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(m)}
                                data-testid={`edit-btn-${m.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openArchiveModal(m)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`archive-btn-${m.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {m.archived_at && (
                            <Badge variant="outline" className="text-red-600">Archived</Badge>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent data-testid="create-modal">
          <DialogHeader>
            <DialogTitle>Create New Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mapping Type</Label>
              <Select 
                value={form.mapping_type} 
                onValueChange={(val) => setForm(prev => ({ ...prev, mapping_type: val, internal_id: "" }))}
              >
                <SelectTrigger data-testid="create-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLIENT">Client</SelectItem>
                  <SelectItem value="CAREGIVER">Caregiver</SelectItem>
                  <SelectItem value="VISIT">Visit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Sandata ID *</Label>
              <Input
                value={form.sandata_id}
                onChange={(e) => setForm(prev => ({ ...prev, sandata_id: e.target.value }))}
                placeholder="ID from Sandata portal"
                data-testid="create-sandata-id"
              />
            </div>
            
            <div>
              <Label>Sandata Name (optional)</Label>
              <Input
                value={form.sandata_name}
                onChange={(e) => setForm(prev => ({ ...prev, sandata_name: e.target.value }))}
                placeholder="Name as shown in Sandata"
              />
            </div>
            
            <div>
              <Label>Internal Record *</Label>
              {form.mapping_type === "VISIT" ? (
                <Input
                  value={form.internal_id}
                  onChange={(e) => setForm(prev => ({ ...prev, internal_id: e.target.value }))}
                  placeholder="Enter internal visit ID"
                  data-testid="create-internal-id"
                />
              ) : (
                <Select 
                  value={form.internal_id} 
                  onValueChange={(val) => {
                    const opt = getInternalOptions().find(o => o.value === val);
                    setForm(prev => ({ 
                      ...prev, 
                      internal_id: val,
                      internal_name: opt?.label || ""
                    }));
                  }}
                >
                  <SelectTrigger data-testid="create-internal-select">
                    <SelectValue placeholder="Select internal record" />
                  </SelectTrigger>
                  <SelectContent>
                    {getInternalOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-submit-btn">Create Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent data-testid="edit-modal">
          <DialogHeader>
            <DialogTitle>Edit Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mapping Type</Label>
              <Input value={MAPPING_TYPE_LABELS[form.mapping_type]?.label || form.mapping_type} disabled />
            </div>
            
            <div>
              <Label>Sandata ID</Label>
              <Input value={form.sandata_id} disabled />
            </div>
            
            <div>
              <Label>Internal Record</Label>
              {form.mapping_type === "VISIT" ? (
                <Input
                  value={form.internal_id}
                  onChange={(e) => setForm(prev => ({ ...prev, internal_id: e.target.value }))}
                  placeholder="Enter internal visit ID"
                />
              ) : (
                <Select 
                  value={form.internal_id} 
                  onValueChange={(val) => {
                    const opt = getInternalOptions().find(o => o.value === val);
                    setForm(prev => ({ 
                      ...prev, 
                      internal_id: val,
                      internal_name: opt?.label || ""
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select internal record" />
                  </SelectTrigger>
                  <SelectContent>
                    {getInternalOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdate} data-testid="edit-submit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Modal */}
      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <DialogContent data-testid="archive-modal">
          <DialogHeader>
            <DialogTitle>Archive Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will archive the mapping between Sandata ID &quot;{selectedMapping?.sandata_id}&quot; 
              and internal record. This action is logged in the audit trail.
            </p>
            <div>
              <Label>Reason for archiving *</Label>
              <Textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="Enter reason (min 5 characters)"
                data-testid="archive-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveModal(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleArchive}
              disabled={archiveReason.length < 5}
              data-testid="archive-submit-btn"
            >
              Archive Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};

export default SandataMappingsPage;
